/**
 * The C# `BackgammonExtention` static methods (BackgammonCommon.cs), ported as free functions taking
 * the receiver as the first argument. Pure helpers used by the engine — keep identical to the C#.
 */

import {
  BeadColor,
  BACKGAMMON_START_ROW,
  BACKGAMMON_ROWS_COUNT,
  BACKGAMMON_WAIT_ROW_WHITE,
  BACKGAMMON_WAIT_ROW_BLACK,
  BACKGAMMON_OUT_ROW_WHITE,
  BACKGAMMON_OUT_ROW_BLACK,
} from './constants';
import type { BeadRow, BeadPack } from './types';

/**
 * The target row a checker on `row` reaches with `dice`, or null. White counts up (bears off at +24),
 * Black counts down (bears off at -1). beadColor overrides the row's own colour (defaults to it).
 */
export function destination(row: BeadRow, dice: number, beadColor: BeadColor = BeadColor.NotSet): number | null {
  const color = beadColor === BeadColor.NotSet ? row.color : beadColor;
  let target: number | null = null;
  if (color === BeadColor.White) {
    target = row.rowIndex + dice;
    if (target === BACKGAMMON_START_ROW + BACKGAMMON_ROWS_COUNT) {
      target = BACKGAMMON_OUT_ROW_WHITE;
    }
  }
  if (color === BeadColor.Black) {
    target = row.rowIndex - dice;
    if (target === BACKGAMMON_START_ROW - 1) {
      target = BACKGAMMON_OUT_ROW_BLACK;
    }
  }
  return target;
}

export function isValidGameRow(target: number | null): boolean {
  if (target === null) {
    return false;
  }
  return target >= BACKGAMMON_START_ROW && target < BACKGAMMON_START_ROW + BACKGAMMON_ROWS_COUNT;
}

/** The 24 rows are four blocks of six. blockNumber null = "any game row". */
export function isInBlock(row: number, blockNumber?: number): boolean {
  switch (blockNumber) {
    case 1:
      return row >= BACKGAMMON_START_ROW && row < BACKGAMMON_START_ROW + 6;
    case 2:
      return row >= BACKGAMMON_START_ROW + 6 && row < BACKGAMMON_START_ROW + 12;
    case 3:
      return row >= BACKGAMMON_START_ROW + 12 && row < BACKGAMMON_START_ROW + 18;
    case 4:
      return row >= BACKGAMMON_START_ROW + 18 && row < BACKGAMMON_START_ROW + 24;
    case undefined:
      return row >= BACKGAMMON_START_ROW && row < BACKGAMMON_START_ROW + BACKGAMMON_ROWS_COUNT;
    default:
      return false;
  }
}

export function isInWaitRow(row: number): boolean {
  return row === BACKGAMMON_WAIT_ROW_WHITE || row === BACKGAMMON_WAIT_ROW_BLACK;
}

export function hasWaitingBead(beadPack: BeadPack, beadColor: BeadColor): boolean {
  return beadPack.waitRow.get(beadColor)!.count > 0;
}

/**
 * True if `color` still has checkers OUTSIDE its home board (so it cannot yet bear off). White's home
 * is rows 18-23 → check 0..17; Black's home is rows 0-5 → check 6..23.
 */
export function hasBeadsInGameRows(beads: BeadPack, color: BeadColor): boolean {
  const startRow = color === BeadColor.Black ? BACKGAMMON_START_ROW + 6 : BACKGAMMON_START_ROW;
  const endRow = color === BeadColor.Black ? BACKGAMMON_START_ROW + 24 : BACKGAMMON_START_ROW + 18;
  for (let i = startRow; i < endRow; i++) {
    if (beads.gameRows[i].color === color) {
      return true;
    }
  }
  return false;
}

/** Order-independent, count-sensitive multiset equality ([5,5] !== [5]). */
export function haveSameItems(array1: number[], array2: number[]): boolean {
  if (array1.length !== array2.length) {
    return false;
  }
  const count = (arr: number[]): Map<number, number> => {
    const m = new Map<number, number>();
    for (const v of arr) {
      m.set(v, (m.get(v) ?? 0) + 1);
    }
    return m;
  };
  const m1 = count(array1);
  const m2 = count(array2);
  if (m1.size !== m2.size) {
    return false;
  }
  for (const [k, c] of m1) {
    if (m2.get(k) !== c) {
      return false;
    }
  }
  return true;
}

export function addDice(value: number, dice: number): number {
  return value + dice;
}
